// Payload público das abas do ensaio, para o SSR das páginas de modelo.
//
// O que o servidor busca vai para o __NUXT_DATA__ do HTML, que qualquer um lê e
// que fica em cache. Por isso cada função monta o objeto a partir de uma lista
// permitida: só sai o que o visitante anônimo já recebe hoje e que a página
// precisa para renderizar. O resto (álbuns, zoom real, caminhos de vídeo) fica
// para a busca do cliente, que passa pelo `api` com o crachá.
//
// Regras:
// - URL assinada (`token=`/`expires=`) nunca entra. Na foto aberta, invalida o
//   payload inteiro (a página cai no fluxo de cliente de hoje).
// - Miniatura base64 das seções fechadas entra até o orçamento, em prefixo
//   (parte 0 → N). A partir da primeira que não cabe, vira `null` e conta em
//   `omitidas`; o cliente completa com `completarMiniaturas`.
// - Resposta de erro (`message`, os v2 devolvem 200 nesse caso) ou fora do
//   formato que os componentes leem direto → `null`.
//
// Sem imports: roda no Nuxt e no node:test.

export const ORCAMENTO_BASE64_PADRAO = 72 * 1024;

type Bruto = Record<string, unknown>;
type Colecao<T> = T[] | Record<string, T>;

export interface FlagsPublicas {
  hasVideos: boolean;
  hasMakingOf: boolean;
  hasLives: boolean;
}

export interface SessaoPublica {
  name: string;
  block: "true" | "false";
  sectionId: number | null;
  photosId: number[];
  photos?: string[];
}

export interface PartePublicaFotos {
  id: number | null;
  name: string;
  sessions: SessaoPublica[];
}

export interface FotosPublicas extends FlagsPublicas {
  shoot: PartePublicaFotos[];
  firstPhotoSection: Colecao<Colecao<string | null>>;
  zoom: false;
  incompleto: boolean;
  omitidas: number;
}

export interface VideoPublico {
  id: number | null;
  name: string;
}

export interface PartePublicaVideos {
  id: number | null;
  name: string;
  has4K: boolean;
  partThumb: string;
  videos: VideoPublico[];
}

export interface VideosPublicos extends FlagsPublicas {
  shoot: PartePublicaVideos[];
}

export interface PerfilPublico extends FlagsPublicas {
  profileQuestion: string;
}

const ASSINADA = /token=|expires=/i;
const URL_HTTP = /^https?:\/\/[^/\s]+/i;
const BASE64_IMAGEM = /^data:image\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+$/i;

function ehObjeto(v: unknown): v is Bruto {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function texto(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function inteiro(v: unknown): number | null {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  if (typeof v === "string" && /^\d+$/.test(v)) return Number(v);
  return null;
}

function respostaValida(raw: unknown): raw is Bruto {
  return ehObjeto(raw) && !("message" in raw);
}

function flags(raw: Bruto): FlagsPublicas {
  return {
    hasVideos: Boolean(raw.hasVideos),
    hasMakingOf: Boolean(raw.hasMakingOf),
    hasLives: Boolean(raw.hasLives),
  };
}

// Mantém o formato que veio: lista continua lista; objeto (o PHP manda objeto
// quando falta índice, por exemplo parte sem seções) continua objeto, com as
// mesmas chaves. A ordem de visita é a das partes/seções (chaves numéricas).
function mapear<T>(col: unknown, f: (v: unknown, chave: string) => T): Colecao<T> | null {
  if (Array.isArray(col)) return col.map((v, i) => f(v, String(i)));
  if (ehObjeto(col)) {
    return Object.fromEntries(Object.entries(col).map(([k, v]) => [k, f(v, k)]));
  }
  return null;
}

function ler(col: unknown, chave: string): unknown {
  if (Array.isArray(col)) return col[Number(chave)];
  if (ehObjeto(col)) return col[chave];
  return undefined;
}

/** A URL, se for absoluta http(s) e não assinada; senão `null`. */
export function urlPublicaSegura(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const url = v.trim();
  if (!URL_HTTP.test(url) || ASSINADA.test(url)) return null;
  return url;
}

function sessaoPublica(s: unknown): SessaoPublica | null {
  if (!ehObjeto(s)) return null;
  const sessao: SessaoPublica = {
    name: texto(s.name),
    block: s.block === true || s.block === "true" ? "true" : "false",
    sectionId: inteiro(s.sectionId),
    photosId: Array.isArray(s.photosId)
      ? s.photosId.map(inteiro).filter((id): id is number => id !== null)
      : [],
  };
  if (Array.isArray(s.photos)) {
    const photos = s.photos.map(urlPublicaSegura);
    if (photos.some((p) => p === null)) return null;
    sessao.photos = photos as string[];
  }
  return sessao;
}

function parteFotos(p: unknown): PartePublicaFotos | null {
  if (!ehObjeto(p)) return null;
  const sessions = Array.isArray(p.sessions) ? p.sessions.map(sessaoPublica) : [];
  if (sessions.some((s) => s === null)) return null;
  return {
    id: inteiro(p.id),
    name: texto(p.name),
    sessions: sessions as SessaoPublica[],
  };
}

/**
 * Payload público de `v2/modelos/{url}/photos`. `null` quando a resposta é de
 * erro ou não serve para renderizar a 1ª seção (`Photos.vue` lê
 * `shoot[0].sessions[0].photos` direto).
 */
export function publicPhotosPayload(
  raw: unknown,
  { orcamentoBase64 = ORCAMENTO_BASE64_PADRAO }: { orcamentoBase64?: number } = {},
): FotosPublicas | null {
  if (!respostaValida(raw)) return null;
  if (!Array.isArray(raw.shoot) || raw.shoot.length === 0) return null;

  const shoot = raw.shoot.map(parteFotos);
  if (shoot.some((p) => p === null)) return null;
  const parte0 = shoot[0] as PartePublicaFotos;
  if (parte0.sessions.length === 0) return null;
  if (!Array.isArray(parte0.sessions[0].photos)) return null;

  let restante = orcamentoBase64;
  let cortou = false;
  let omitidas = 0;
  const miniatura = (v: unknown): string | null => {
    if (typeof v !== "string") return null;
    if (!v.startsWith("data:")) return urlPublicaSegura(v);
    if (!BASE64_IMAGEM.test(v)) return null;
    if (cortou || v.length > restante) {
      cortou = true;
      omitidas++;
      return null;
    }
    restante -= v.length;
    return v;
  };
  const firstPhotoSection = mapear(raw.firstPhotoSection, (parte) =>
    mapear(parte, miniatura) ?? [],
  );
  if (!firstPhotoSection) return null;

  return {
    shoot: shoot as PartePublicaFotos[],
    firstPhotoSection,
    ...flags(raw),
    zoom: false,
    incompleto: omitidas > 0,
    omitidas,
  };
}

/**
 * Preenche as miniaturas que o SSR omitiu com as da resposta completa que o
 * cliente buscou. Só troca `null`; devolve um objeto novo (reatividade).
 */
export function completarMiniaturas(pub: FotosPublicas, completo: unknown): FotosPublicas {
  const fonte = ehObjeto(completo) ? completo.firstPhotoSection : undefined;
  const firstPhotoSection = mapear(pub.firstPhotoSection, (parte, p) =>
    mapear(parte, (valor, s) => {
      const novo = ler(ler(fonte, p), s);
      return valor === null && typeof novo === "string" ? novo : (valor as string | null);
    }) ?? [],
  ) as Colecao<Colecao<string | null>>;
  return { ...pub, firstPhotoSection, incompleto: false, omitidas: 0 };
}

/**
 * Payload público de `v3/modelos/{url}/videos`: só o que monta a casca da aba
 * (partes, nomes, pôster). `videoPath`, `urlPanda` e `processing` ficam de fora;
 * a URL de reprodução vem só da busca do cliente.
 */
export function publicVideosPayload(raw: unknown): VideosPublicos | null {
  if (!respostaValida(raw)) return null;
  if (!Array.isArray(raw.shoot) || raw.shoot.length === 0) return null;

  const shoot = raw.shoot.map((p): PartePublicaVideos | null => {
    if (!ehObjeto(p)) return null;
    return {
      id: inteiro(p.id),
      name: texto(p.name),
      has4K: p.has4K === true,
      partThumb: urlPublicaSegura(p.partThumb) ?? "",
      videos: Array.isArray(p.videos)
        ? p.videos.filter(ehObjeto).map((v) => ({ id: inteiro(v.id), name: texto(v.name) }))
        : [],
    };
  });
  if (shoot.some((p) => p === null)) return null;

  return { shoot: shoot as PartePublicaVideos[], ...flags(raw) };
}

/**
 * Payload público de `v2/modelos/{url}/perfilWallpaperCredits` para o texto de
 * perfil. Cada aba que usa esse endpoint (perfil, créditos, wallpapers) recebe
 * só a sua parte da resposta.
 */
export function publicPerfilPayload(raw: unknown): PerfilPublico | null {
  if (!respostaValida(raw)) return null;
  const perfil = texto(raw.profileQuestion);
  return {
    profileQuestion: ASSINADA.test(perfil) ? "" : perfil,
    ...flags(raw),
  };
}

export interface CreditoPublico {
  functions: string;
  persons: string;
}

export interface CreditosPublicos extends FlagsPublicas {
  credits: CreditoPublico[];
}

/** Payload público de `perfilWallpaperCredits` para a aba de créditos. */
export function publicCreditsPayload(raw: unknown): CreditosPublicos | null {
  if (!respostaValida(raw)) return null;
  const credits = Array.isArray(raw.credits)
    ? raw.credits
        .filter(ehObjeto)
        .map((c) => ({ functions: texto(c.functions), persons: texto(c.persons) }))
    : [];
  return { credits, ...flags(raw) };
}

export interface WallpaperPublico {
  id: number | null;
  imgPath: string;
}

export interface WallpapersPublicos extends FlagsPublicas {
  cardsWallPappers: WallpaperPublico[];
}

/**
 * Payload público de `perfilWallpaperCredits` para a aba de wallpapers: só a
 * capa de cada um. Os links de download (`imgDesktop`/`imgMobile`, rotas do
 * api.) ficam para a busca do cliente. Capa sem URL segura não entra.
 */
export function publicWallpapersPayload(raw: unknown): WallpapersPublicos | null {
  if (!respostaValida(raw)) return null;
  const cardsWallPappers = Array.isArray(raw.cardsWallPappers)
    ? raw.cardsWallPappers
        .filter(ehObjeto)
        .map((w) => ({ id: inteiro(w.id), imgPath: urlPublicaSegura(w.imgPath) ?? "" }))
        .filter((w) => w.imgPath !== "")
    : [];
  return { cardsWallPappers, ...flags(raw) };
}

export interface ParteMakingOfPublica {
  id: number | null;
  name: string;
  partThumb: string;
  lives: VideoPublico[];
}

export interface MakingOfPublico extends FlagsPublicas {
  shoot: ParteMakingOfPublica[];
}

/**
 * Payload público de `v3/modelos/{url}/makingOf`: partes, nomes e miniatura.
 * `livePath`, `urlPanda` e `processing` ficam de fora; a URL de reprodução vem
 * só da busca do cliente, que também emite o crachá do vídeo.
 */
export function publicMakingOfPayload(raw: unknown): MakingOfPublico | null {
  if (!respostaValida(raw)) return null;
  if (!Array.isArray(raw.shoot) || raw.shoot.length === 0) return null;

  const shoot = raw.shoot.map((p): ParteMakingOfPublica | null => {
    if (!ehObjeto(p)) return null;
    return {
      id: inteiro(p.id),
      name: texto(p.name),
      partThumb: urlPublicaSegura(p.partThumb) ?? "",
      lives: Array.isArray(p.lives)
        ? p.lives.filter(ehObjeto).map((l) => ({ id: inteiro(l.id), name: texto(l.name) }))
        : [],
    };
  });
  if (shoot.some((p) => p === null)) return null;

  return { shoot: shoot as ParteMakingOfPublica[], ...flags(raw) };
}

export interface LivesPublicas extends FlagsPublicas {
  liveVideos: VideoPublico[];
}

/**
 * Payload público de `v2/modelos/{url}/lives`: id e nome de cada live.
 * `livePath` (prévia assinada), `path`, `urlPanda` e `processing` ficam de fora.
 */
export function publicLivesPayload(raw: unknown): LivesPublicas | null {
  if (!respostaValida(raw)) return null;
  if (!Array.isArray(raw.liveVideos)) return null;
  const liveVideos = raw.liveVideos
    .filter(ehObjeto)
    .map((l) => ({ id: inteiro(l.id), name: texto(l.name) }));
  return { liveVideos, ...flags(raw) };
}
